import { useState, useRef, useEffect, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { S7Logo } from "@/components/landing/S7Logo";
import { BookACallButton } from "@/components/landing/BookACallButton";
import { useNavThemeFlip } from "@/components/landing/useNavThemeFlip";
import { smoothScrollTo } from "@/components/landing/home/smooth-scroll";
import {
  ArrowLeft,
  BookOpen,
  CircleHelp,
  ClipboardPlus,
  Info,
  FileText,
  Newspaper,
  PenLine,
  Shield,
  ShieldCheck,
  Workflow,
} from "lucide-react";

export const NAVBAR_HEIGHT = 64;

// On-dark theme channels (bare HSL triples, matching theme.css). When the nav
// sits over a dark section we override --foreground / --background on the <nav>
// element so every child reading hsl(var(--foreground)) / hsl(var(--background))
// inverts atomically.
const DARK_FOREGROUND = "216 30% 96%";
const DARK_BACKGROUND = "225 50% 3.5%";

// Light page theme channels (theme.css :root). Pinned onto the help dropdown so
// its panel + text stay legible even when the nav itself flips dark.
const LIGHT_FOREGROUND = "225 45% 14%";
const LIGHT_BACKGROUND = "38 30% 96%";

interface LandingNavbarProps {
  isProduct: boolean;
  onBack: () => void;
  /**
   * Optional CSS selector for dark sections. When provided, the nav flips to a
   * dark theme while any matching element is under the top of the nav. When
   * UNDEFINED, the nav behaves exactly as before (no flip). Only the homepage
   * passes this.
   */
  darkSectionSelector?: string;
  /**
   * A bar above the nav, `bannerHeight` tall (the homepage's FreeEhrBanner).
   * The two share a sticky header resting at minus the bar's height, so the
   * bar scrolls away and the nav stays pinned. A negative bottom margin keeps
   * the header out of the flow, so the page still starts under it as it does
   * under the fixed nav; the page adds the bar's height to its top padding.
   */
  banner?: ReactNode;
  bannerHeight?: number;
}

type HelpMenuItem =
  | { label: string; icon: typeof Info; href: string }
  | { type: "divider" };

// Quieter destinations kept in the "?" dropdown, under Blog and News. The
// primary content links (How it works, Free EHR, About) live in the navbar
// itself. No referral link anywhere: offering referrals is barred by
// anti-kickback statutes.
const helpMenuItems: HelpMenuItem[] = [
  { label: "Documentation", icon: BookOpen, href: "/docs" },
  { label: "HIPAA compliance", icon: ShieldCheck, href: "/hipaa" },
  { type: "divider" },
  { label: "Terms of service", icon: FileText, href: "/terms" },
  { label: "Privacy policy", icon: Shield, href: "/privacy" },
];

export function LandingNavbar({
  isProduct,
  onBack,
  darkSectionSelector,
  banner,
  bannerHeight = 0,
}: LandingNavbarProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Whether the nav currently overlaps a dark section (theme-flip). Stays false
  // for every caller that does not pass darkSectionSelector.
  const dark = useNavThemeFlip(darkSectionSelector);

  useEffect(() => {
    if (!helpOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setHelpOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [helpOpen]);

  // "How it works" points at the on-page billing-loop section. On the homepage
  // it smooth-scrolls there; from any other route it navigates home (the loop
  // is part of the homepage). Closes the dropdown if it was the trigger.
  function goToHowItWorks(e: React.MouseEvent) {
    e.preventDefault();
    setHelpOpen(false);
    const el = document.getElementById("how");
    if (el) {
      smoothScrollTo(el, 1200, NAVBAR_HEIGHT);
    } else {
      navigate("/");
    }
  }

  // News is served by the news-renderer SSR service (outside React), so it
  // always uses a full-page navigation.
  function goToNews(e: React.MouseEvent) {
    e.preventDefault();
    setHelpOpen(false);
    window.location.href = "/news";
  }

  // The blog is committed static HTML under /docs, served from Blob Storage
  // behind the App Gateway, so React never renders it. A <Link> would be
  // client-routed into the SPA's catch-all and land on "/" instead, the same
  // reason /news uses a hard navigation.
  function goToBlog(e: React.MouseEvent) {
    e.preventDefault();
    setHelpOpen(false);
    window.location.href = "/docs/blog";
  }

  const navLinkClass =
    "inline-flex items-center justify-center text-[14px] font-medium text-foreground transition-all duration-150 no-underline hover:opacity-60 cursor-pointer bg-transparent border-0";

  const nav = (
    <nav
      className={banner ? "relative" : "fixed top-0 left-0 right-0 z-50"}
      style={{
        height: NAVBAR_HEIGHT,
        background: "hsl(var(--background) / 0.8)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        transition: "background .4s ease, color .4s ease",
        // Theme-flip: overriding the channel triples here cascades to the
        // logo wordmark, the content links, the help icon, and the Book a demo
        // pill, all of which read from hsl(var(--foreground)) /
        // hsl(var(--background)). When light we set nothing so the page's :root
        // light values inherit.
        ...(dark
          ? ({
              "--foreground": DARK_FOREGROUND,
              "--background": DARK_BACKGROUND,
            } as React.CSSProperties)
          : null),
      }}
      aria-label="Landing navigation"
    >
      <div className="relative mx-auto h-full" style={{ maxWidth: 1440 }}>
        {isProduct && (
          <button
            onClick={onBack}
            className="absolute left-5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 cursor-pointer bg-transparent border-0 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Back to home"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        )}

        <button
          onClick={onBack}
          className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2 sm:gap-3 text-foreground bg-transparent border-0 cursor-pointer left-4 sm:left-[88px]"
          aria-label="Altair home"
        >
          <span className="hidden sm:inline-flex">
            <S7Logo size={36} />
          </span>
          <span className="sm:hidden inline-flex">
            <S7Logo size={28} />
          </span>
          <span className="text-[20px] sm:text-[26px] font-semibold tracking-tight">
            Altair
          </span>
        </button>

        <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-3.5">
          {/* Desktop content links — let visitors learn what Altair does before
              the single demo CTA. Hidden on mobile, where they move into the
              help dropdown. */}
          <div className="hidden sm:flex items-center gap-6">
            <a href="#how" className={navLinkClass} onClick={goToHowItWorks}>
              How it works
            </a>
            {/* A pill in the banner tag's emerald, so the free EHR stands out
                from the text links. From 768px; narrower, the row would
                reach the logo, so the link moves into the dropdown. */}
            <Link
              to="/ehr"
              className="hidden md:inline-flex items-center rounded-full text-[14px] font-medium text-foreground no-underline whitespace-nowrap transition-colors duration-150 border border-[hsl(152_55%_55%/0.4)] bg-[hsl(152_55%_55%/0.14)] hover:bg-[hsl(152_55%_55%/0.24)]"
              style={{ padding: "6px 14px" }}
              onClick={() => setHelpOpen(false)}
            >
              Free EHR
            </Link>
            <Link
              to="/about"
              className={navLinkClass}
              onClick={() => setHelpOpen(false)}
            >
              About
            </Link>
          </div>

          {/* Pipe separating the browse links from the primary action, so the
              CTA reads as distinct rather than as another link. Desktop only;
              on mobile the links live in the dropdown. */}
          <span
            aria-hidden="true"
            className="hidden sm:inline-block"
            style={{
              width: 2,
              height: 18,
              borderRadius: 1,
              background: "hsl(var(--foreground) / 0.25)",
            }}
          />

          {/* The single primary CTA, all screen sizes. */}
          <BookACallButton variant="filled" placement="navbar" />

          <div ref={helpRef} className="relative">
            <button
              onClick={() => setHelpOpen((prev) => !prev)}
              className="inline-flex items-center justify-center w-11 h-11 rounded-full cursor-pointer border-0 bg-transparent text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-all duration-150"
              aria-label="Help and info"
              aria-expanded={helpOpen}
              aria-haspopup="true"
            >
              <CircleHelp className="w-5 h-5" />
            </button>

            {helpOpen && (
              <div
                className="absolute right-0 top-full mt-2 py-2 z-50"
                role="menu"
                style={
                  {
                    minWidth: 220,
                    borderRadius: 16,
                    // Re-pin the channel triples to the light page values so the
                    // panel + all its text stay legible even when the nav itself
                    // has flipped to the dark theme over a dark section.
                    "--foreground": LIGHT_FOREGROUND,
                    "--background": LIGHT_BACKGROUND,
                    background: "hsl(var(--background))",
                    boxShadow:
                      "0 0 0 1px hsl(var(--border) / 0.5), var(--shadow-lg)",
                  } as React.CSSProperties
                }
              >
                {/* Mobile-only: the navbar's content links (How it works,
                    About) move here so they stay reachable on phones without
                    crowding the header, and Free EHR up to 768px. Book a demo
                    stays the visible header CTA. */}
                <a
                  href="#how"
                  role="menuitem"
                  className="sm:hidden flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground/80 hover:text-foreground transition-colors duration-100 no-underline"
                  onClick={goToHowItWorks}
                >
                  <Workflow className="w-4 h-4 text-muted-foreground" />
                  <span>How it works</span>
                </a>
                <a
                  href="/ehr"
                  role="menuitem"
                  className="md:hidden flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground/80 hover:text-foreground transition-colors duration-100 no-underline"
                  onClick={(e) => {
                    e.preventDefault();
                    setHelpOpen(false);
                    navigate("/ehr");
                  }}
                >
                  <ClipboardPlus className="w-4 h-4 text-muted-foreground" />
                  <span>Free EHR</span>
                </a>
                <a
                  href="/about"
                  role="menuitem"
                  className="sm:hidden flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground/80 hover:text-foreground transition-colors duration-100 no-underline"
                  onClick={(e) => {
                    e.preventDefault();
                    setHelpOpen(false);
                    navigate("/about");
                  }}
                >
                  <Info className="w-4 h-4 text-muted-foreground" />
                  <span>About us</span>
                </a>
                <div
                  className="md:hidden my-1.5"
                  role="separator"
                  style={{
                    height: 1,
                    background: "hsl(var(--border) / 0.3)",
                  }}
                />
                <a
                  href="/docs/blog"
                  role="menuitem"
                  className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground/80 hover:text-foreground transition-colors duration-100 no-underline"
                  onClick={goToBlog}
                >
                  <PenLine className="w-4 h-4 text-muted-foreground" />
                  <span>Blog</span>
                </a>
                <a
                  href="/news"
                  role="menuitem"
                  className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground/80 hover:text-foreground transition-colors duration-100 no-underline"
                  onClick={goToNews}
                >
                  <Newspaper className="w-4 h-4 text-muted-foreground" />
                  <span>News</span>
                </a>

                {helpMenuItems.map((item, idx) =>
                  "type" in item && item.type === "divider" ? (
                    <div
                      key={`divider-${idx}`}
                      className="my-1.5"
                      role="separator"
                      style={{
                        height: 1,
                        background: "hsl(var(--border) / 0.3)",
                      }}
                    />
                  ) : (
                    <a
                      key={"label" in item ? item.label : idx}
                      href={"href" in item ? item.href : "#"}
                      role="menuitem"
                      className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground/80 hover:text-foreground transition-colors duration-100 no-underline"
                      onClick={(e) => {
                        e.preventDefault();
                        setHelpOpen(false);
                        if ("href" in item && item.href) {
                          // Static doc pages live outside React — full nav
                          if (item.href.startsWith("/docs")) {
                            window.location.href = item.href;
                          } else {
                            navigate(item.href);
                          }
                        }
                      }}
                    >
                      {"icon" in item && item.icon && (
                        <item.icon className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span>{"label" in item ? item.label : ""}</span>
                    </a>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );

  if (!banner) return nav;
  return (
    <div
      className="sticky z-50"
      style={{ top: -bannerHeight, marginBottom: -(bannerHeight + NAVBAR_HEIGHT) }}
    >
      {banner}
      {nav}
    </div>
  );
}
